import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import isEqual from 'lodash/isEqual';
import useSWR, { KeyedMutator } from 'swr';
import useSWRInfinite, { SWRInfiniteKeyedMutator } from 'swr/infinite';

import {
  OrbitAnswer,
  OrbitCreateRequest,
  OrbitListResponse,
  OrbitOutcome,
  OrbitSession,
  OrbitSessionChanges,
  OrbitSessionListResponse,
  OrbitSessionResponse,
  OrbitSessionsParams,
  OrbitState,
  OrbitTurn,
  OrbitTurnResponse,
  OrbitTurnsParams
} from 'containers/Orbit/types';
import API from 'utils/request';

import { buildQuery, fetcher } from './utils';

export const ORBIT_REQUESTS_PATH = '/v4/dash/orbit/requests';
export const ORBIT_SESSIONS_PATH = '/v4/dash/orbit/sessions';

export const ORBIT_POLL_INTERVAL_MS = 5000;

// Contract §7 default; the param is `size` (hooks/patterns.md), never `per_page` or the `limit` alias.
export const ORBIT_LIST_PAGE_SIZE = 25;

export const ORBIT_TERMINAL_STATES: ReadonlySet<OrbitState> = new Set<OrbitState>([
  'completed',
  'failed',
  'timeout'
]);

export const isTerminalOrbitState = (state: OrbitState | undefined): boolean =>
  state !== undefined && ORBIT_TERMINAL_STATES.has(state);

export const hasOrbitAnswer = (
  turn: OrbitTurn | undefined
): turn is OrbitTurn & { answer: OrbitAnswer } =>
  turn?.state === 'completed' && typeof turn.answer.answer === 'string';

export const orbitTurnPath = (id: number | string) =>
  `${ORBIT_REQUESTS_PATH}/${encodeURIComponent(id)}`;

export const orbitTurnsPath = (params: OrbitTurnsParams) =>
  `${ORBIT_REQUESTS_PATH}?${buildQuery(params)}`;

export type OrbitMutationFailure =
  // 403: flag or section permission off, or a `session_id` owned by another user.
  | { kind: 'forbidden'; message: string | null }
  // 404 `40410`: unknown id, another user's turn, or a cleared conversation.
  | { kind: 'notFound'; message: string | null }
  // 422 `4221`: blank / over-long question.
  | { kind: 'invalid'; message: string | null }
  // 401: the bearer token expired between the last call and this one.
  | { kind: 'sessionExpired'; message: string | null }
  // No response at all: the browser is offline, DNS/TLS failed, or the request was aborted.
  | { kind: 'network'; message: string | null }
  // 5xx: the request reached a server and a server refused it.
  | { kind: 'serverError'; message: string | null }
  | { kind: 'technicalError'; message: string | null };

export type OrbitMutationOutcome<T> = { kind: 'ok'; data: T } | OrbitMutationFailure;

const messageFromError = (error: any): string | null => {
  const message = error?.response?.data?.message;

  return typeof message === 'string' && message.length > 0 ? message : null;
};

/**
 * True when axios rejected without ever receiving a response — offline, DNS/TLS failure, the
 * request aborted, or an `ECONNABORTED` timeout. The outcome of such a POST is UNKNOWN, not
 * un-received: the server saves the turn before it answers, so a connection lost after that point
 * still created it.
 */
const isNoResponseError = (error: any): boolean =>
  error?.isAxiosError === true && error?.response === undefined;

/** Status and code of the failure, for the next occurrence. Silent outside a dev server. */
const logOrbitFailure = (label: string, error: any): void => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(`[orbit] ${label} failed`, {
    status: error?.response?.status ?? null,
    code: error?.code ?? null,
    message: error?.message ?? null
  });
};

// A 401 reaches here as well as the response interceptor: the interceptor starts the sign-out and
// re-rejects, so the caller still has to say why the question did not go.
export const orbitFailureFromError = (error: any): OrbitMutationFailure => {
  const status = error?.response?.status;
  const message = messageFromError(error);

  if (isNoResponseError(error)) {
    return { kind: 'network', message: null };
  }

  if (status === 401) {
    return { kind: 'sessionExpired', message };
  }

  if (status === 403) {
    return { kind: 'forbidden', message };
  }

  if (status === 404) {
    return { kind: 'notFound', message };
  }

  if (status === 422) {
    return { kind: 'invalid', message };
  }

  if (typeof status === 'number' && status >= 500) {
    return { kind: 'serverError', message };
  }

  return { kind: 'technicalError', message };
};

/**
 * The create only saves the turn and enqueues its job; the answer is polled for afterwards, so a
 * POST still unanswered after this long is treated as one that got no response. Without it a
 * request that never settles keeps the composer locked for good.
 */
export const ORBIT_ASK_TIMEOUT_MS = 30000;

/**
 * Sends the question ONCE. The POST is not idempotent — every accepted call creates a turn — so
 * nothing here sends it again: not after a 5xx, which may come from a gateway that already handed
 * the request on, and not after no response, which the server may have saved all the same. The
 * caller settles a `network` outcome with `findLandedOrbitTurn` before the question goes again.
 */
export const createOrbitRequest = async (
  payload: OrbitCreateRequest
): Promise<OrbitMutationOutcome<OrbitTurn>> => {
  try {
    const response = await API.post<OrbitTurnResponse>(ORBIT_REQUESTS_PATH, payload, {
      timeout: ORBIT_ASK_TIMEOUT_MS
    });
    const created = response?.data?.data;

    // A 2xx whose body carries no turn is not a success: the caller reads `id` and `session_id` off
    // it immediately, and handing it `undefined` throws inside the state write instead of showing
    // the user an error they can act on.
    if (created === undefined || created === null || typeof created.id !== 'number') {
      logOrbitFailure('createOrbitRequest (malformed body)', null);

      return { kind: 'technicalError', message: null };
    }

    return { kind: 'ok', data: created };
  } catch (error) {
    logOrbitFailure('createOrbitRequest', error);

    return orbitFailureFromError(error);
  }
};

/**
 * The turn a POST that got no response created anyway, if it did: a turn on page 1 of the session
 * (newest first) asking the same question that the caller did not already hold. `null` when there is
 * none, so the question can go again; a failure when the list cannot be read either.
 */
export const findLandedOrbitTurn = async (
  sessionId: string,
  question: string,
  knownTurnIds: readonly number[]
): Promise<OrbitMutationOutcome<OrbitTurn | null>> => {
  try {
    const response = await API.get<OrbitListResponse>(
      orbitTurnsPath({ size: ORBIT_LIST_PAGE_SIZE, session_id: sessionId, page: 1 }),
      { timeout: ORBIT_ASK_TIMEOUT_MS }
    );
    const listed = response?.data?.data;

    if (!Array.isArray(listed)) {
      logOrbitFailure('findLandedOrbitTurn (malformed body)', null);

      return { kind: 'technicalError', message: null };
    }

    const landed = listed.find(
      candidate => candidate.question === question && !knownTurnIds.includes(candidate.id)
    );

    return { kind: 'ok', data: landed ?? null };
  } catch (error) {
    logOrbitFailure('findLandedOrbitTurn', error);

    return orbitFailureFromError(error);
  }
};

/** Chats per page on every chat list: history loads four at a time. */
export const ORBIT_SESSION_PAGE_SIZE = 4;

/** The session list's own cap, used by the one-off lookup below. */
const ORBIT_SESSION_LOOKUP_SIZE = 50;

/** A chat is listed once it holds a completed turn with one of these outcomes. */
const ORBIT_HISTORY_OUTCOMES: ReadonlySet<OrbitOutcome> = new Set<OrbitOutcome>([
  'answered',
  'clarify',
  'report_queued'
]);

/** The turn makes its chat listable, and so renamable, pinnable and fileable. */
export const isOrbitHistoryTurn = (turn: OrbitTurn | undefined): boolean =>
  turn?.state === 'completed' && turn.outcome !== null && ORBIT_HISTORY_OUTCOMES.has(turn.outcome);

export const orbitSessionPath = (sessionId: string) =>
  `${ORBIT_SESSIONS_PATH}/${encodeURIComponent(sessionId)}`;

export const orbitSessionsPath = (params: OrbitSessionsParams) =>
  `${ORBIT_SESSIONS_PATH}?${buildQuery(params)}`;

/*
  Every chat row a list, a lookup or a PATCH has returned, by session id, so a surface showing one
  chat (the chat header, the panel's name bar) reads the title, pin and Workspace the lists show.
  It lives while any reader is mounted, and empties with the last one.
*/
let knownSessions: ReadonlyMap<string, OrbitSession> = new Map();
let knownReaders = 0;
const knownListeners = new Set<() => void>();

const setKnownSessions = (next: ReadonlyMap<string, OrbitSession>) => {
  knownSessions = next;
  knownListeners.forEach(listener => listener());
};

const subscribeKnown = (listener: () => void) => {
  knownListeners.add(listener);
  return () => {
    knownListeners.delete(listener);
  };
};

const rememberSessions = (sessions: OrbitSession[]) => {
  const changed = sessions.filter(listed => !isEqual(knownSessions.get(listed.session_id), listed));

  if (changed.length > 0) {
    const next = new Map(knownSessions);
    changed.forEach(listed => next.set(listed.session_id, listed));
    setKnownSessions(next);
  }
};

/** Sign-out empties the record, so nothing carries over to the next user. */
export const clearKnownOrbitSessions = () => setKnownSessions(new Map());

const useKnownSessionsReader = () =>
  useEffect(() => {
    knownReaders += 1;
    return () => {
      knownReaders -= 1;
      if (knownReaders === 0) {
        clearKnownOrbitSessions();
      }
    };
  }, []);

/** The chat's row as the lists, a lookup or the last PATCH returned it; undefined when none has. */
export const useOrbitSessionRecord = (sessionId: string | null): OrbitSession | undefined => {
  useKnownSessionsReader();
  const known = useSyncExternalStore(subscribeKnown, () => knownSessions);

  return sessionId === null ? undefined : known.get(sessionId);
};

interface IMountedSessionList {
  params: () => OrbitSessionsParams;
  mutate: SWRInfiniteKeyedMutator<OrbitSessionListResponse[]>;
}

// Global `mutate` with a key filter skips `useSWRInfinite` keys, so each list registers its own.
const mountedSessionLists = new Set<IMountedSessionList>();

/** Whether a chat as it is now belongs in a list filtered by `params`. `q` is the server's to judge. */
const listHolds = (listed: OrbitSession, { pinned, workspace }: OrbitSessionsParams): boolean =>
  (pinned === undefined || listed.pinned === pinned) &&
  (workspace === undefined ||
    (workspace === 'none' ? listed.workspace === null : listed.workspace === workspace));

/** Writes the change into every mounted list at once, then refetches each one. */
const rewriteSessionLists = (
  rewrite: (sessions: OrbitSession[], params: OrbitSessionsParams) => OrbitSession[]
) =>
  Promise.all(
    Array.from(mountedSessionLists).map(async ({ params, mutate }) => {
      await mutate(pages => pages?.map(page => ({ ...page, data: rewrite(page.data, params()) })), {
        revalidate: false
      });
      mutate();
    })
  );

/** Refetches every mounted chat list, e.g. once an answer may have listed or moved a chat. */
export const revalidateOrbitSessionLists = () => {
  mountedSessionLists.forEach(({ mutate }) => mutate());
};

const applySessionUpdate = (updated: OrbitSession) => {
  rememberSessions([updated]);

  return rewriteSessionLists((sessions, params) =>
    sessions.flatMap(listed => {
      if (listed.session_id !== updated.session_id) {
        return [listed];
      }
      return listHolds(updated, params) ? [updated] : [];
    })
  );
};

const forgetSession = (sessionId: string) => {
  if (knownSessions.has(sessionId)) {
    const next = new Map(knownSessions);
    next.delete(sessionId);
    setKnownSessions(next);
  }

  return rewriteSessionLists(sessions =>
    sessions.filter(listed => listed.session_id !== sessionId)
  );
};

export const clearOrbitSession = async (sessionId: string): Promise<OrbitMutationOutcome<null>> => {
  try {
    await API.delete(orbitSessionPath(sessionId));
    await forgetSession(sessionId);

    return { kind: 'ok', data: null };
  } catch (error) {
    return orbitFailureFromError(error);
  }
};

/**
 * Renames, pins or files a chat. On success every mounted list and the record take the row the
 * server confirmed; a 404 means the chat is gone, so it leaves them. Any other failure leaves both
 * as they were.
 */
export const updateOrbitSession = async (
  sessionId: string,
  changes: OrbitSessionChanges
): Promise<OrbitMutationOutcome<OrbitSession>> => {
  let updated: OrbitSession | undefined;

  try {
    const response = await API.patch<OrbitSessionResponse>(orbitSessionPath(sessionId), changes);
    updated = response?.data?.data;
  } catch (error) {
    logOrbitFailure('updateOrbitSession', error);
    const failure = orbitFailureFromError(error);

    if (failure.kind === 'notFound') {
      await forgetSession(sessionId);
    }

    return failure;
  }

  if (updated === undefined || updated === null || typeof updated.session_id !== 'string') {
    logOrbitFailure('updateOrbitSession (malformed body)', null);

    return { kind: 'technicalError', message: null };
  }

  await applySessionUpdate(updated);

  return { kind: 'ok', data: updated };
};

export interface UseOrbitSessionsResult {
  /** Every loaded page in API order (latest activity first), each chat once. */
  sessions: OrbitSession[];
  hasMore: boolean;
  /** At least one page has resolved. */
  hasLoaded: boolean;
  /** No page loaded yet and the first request is in flight. */
  isLoading: boolean;
  /** A page beyond the loaded ones is in flight. */
  isLoadingMore: boolean;
  error: any;
  loadMore: () => void;
  /** Refetches every loaded page. */
  reload: () => void;
}

/**
 * A chat list, `ORBIT_SESSION_PAGE_SIZE` chats a page. Pass `null` to skip fetching. The list is
 * recomputed on every request, so a chat with new activity can show up on two pages: it is kept
 * where it was first listed.
 */
export const useOrbitSessions = (params: OrbitSessionsParams | null): UseOrbitSessionsResult => {
  useKnownSessionsReader();
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const listKey = params === null ? null : orbitSessionsPath(params);

  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite<OrbitSessionListResponse>(
      (index: number, previous: OrbitSessionListResponse | null) => {
        if (params === null || (previous !== null && previous.meta?.next_page === null)) {
          return null;
        }

        return orbitSessionsPath({ size: ORBIT_SESSION_PAGE_SIZE, ...params, page: index + 1 });
      },
      fetcher,
      { revalidateOnFocus: false }
    );

  useEffect(() => {
    if (listKey === null) {
      return undefined;
    }

    const mounted: IMountedSessionList = { params: () => paramsRef.current ?? {}, mutate };
    mountedSessionLists.add(mounted);

    return () => {
      mountedSessionLists.delete(mounted);
    };
  }, [listKey, mutate]);

  const sessions = useMemo(() => {
    const seen = new Set<string>();

    return (data ?? [])
      .flatMap(page => page.data)
      .filter(listed => {
        if (seen.has(listed.session_id)) {
          return false;
        }
        seen.add(listed.session_id);
        return true;
      });
  }, [data]);

  useEffect(() => rememberSessions(sessions), [sessions]);

  const lastPage = data !== undefined && data.length > 0 ? data[data.length - 1] : undefined;
  const loadedPages = data?.length ?? 0;
  const loadMore = useCallback(() => setSize(prev => prev + 1), [setSize]);
  const reload = useCallback(() => {
    mutate();
  }, [mutate]);

  return {
    sessions,
    hasMore: lastPage !== undefined && typeof lastPage.meta?.next_page === 'number',
    hasLoaded: data !== undefined,
    isLoading,
    isLoadingMore: isValidating && loadedPages > 0 && loadedPages < size,
    error,
    loadMore,
    reload
  };
};

/**
 * Finds the row of a chat no mounted list holds, such as one reopened after a reload that is filed
 * in a Workspace. There is no read of a single chat, and `q` always matches the chat's own
 * question. Pass `null` once the row is known.
 */
export const useOrbitSessionLookup = (question: string | null): void => {
  useKnownSessionsReader();
  const { data } = useSWR<OrbitSessionListResponse>(
    question === null || question.trim() === ''
      ? null
      : orbitSessionsPath({ q: question, size: ORBIT_SESSION_LOOKUP_SIZE, page: 1 }),
    fetcher,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (Array.isArray(data?.data)) {
      rememberSessions(data.data);
    }
  }, [data]);
};

export const orbitTurnRefreshInterval = (latest: OrbitTurnResponse | undefined): number =>
  isTerminalOrbitState(latest?.data?.state) ? 0 : ORBIT_POLL_INTERVAL_MS;

/** Retries of a failed poll, one interval apart: 24 x 5 s spans the backend's 120 s processing limit. */
export const ORBIT_POLL_RETRY_COUNT = 24;

/** A poll worth repeating: it never reached a server, or a server failed it. */
export const isRetryableOrbitPollError = (error: unknown): boolean => {
  const { kind } = orbitFailureFromError(error);

  return kind === 'network' || kind === 'serverError';
};

/** The turn is gone (404) or no longer readable by this user (403), so no answer will ever come. */
export const isFatalOrbitPollError = (error: unknown): boolean => {
  const { kind } = orbitFailureFromError(error);

  return kind === 'forbidden' || kind === 'notFound';
};

/**
 * How long a turn may stay `queued` or `processing` before it is treated as timed out. The backend
 * derives `timeout` for `processing` only, so a job nobody picked up stays `queued` on the wire
 * forever.
 */
export const ORBIT_PENDING_TURN_LIMIT_MS = 3 * 60 * 1000;

/** When this tab first saw each turn pending, by turn id, on the browser's own clock. */
export type OrbitPendingSince = Map<number, number>;

const pendingSince = (seen: OrbitPendingSince, turn: OrbitTurn): number => {
  const known = seen.get(turn.id);

  if (known !== undefined) {
    return known;
  }

  const now = Date.now();
  seen.set(turn.id, now);

  return now;
};

/** Starts a pending turn's clock now unless this tab already saw it; a terminal turn has none. */
export const markOrbitTurnSeen = (seen: OrbitPendingSince, turn: OrbitTurn): void => {
  if (!isTerminalOrbitState(turn.state)) {
    pendingSince(seen, turn);
  }
};

/**
 * When a pending turn is given up on; `null` for a terminal turn. The limit runs from the later of
 * its `created_at` and the moment this tab first saw it pending, so a browser clock running ahead
 * of the server's cannot time out a fresh turn.
 */
export const orbitTurnExpiresAt = (turn: OrbitTurn, seen: OrbitPendingSince): number | null => {
  if (isTerminalOrbitState(turn.state)) {
    return null;
  }

  const createdAt = Date.parse(turn.created_at);
  const since = pendingSince(seen, turn);

  return (
    (Number.isNaN(createdAt) ? since : Math.max(createdAt, since)) + ORBIT_PENDING_TURN_LIMIT_MS
  );
};

const asTimedOut = (turn: OrbitTurn): OrbitTurn => ({ ...turn, state: 'timeout' });

/**
 * The turns with every one pending past `ORBIT_PENDING_TURN_LIMIT_MS` drawn as `timeout`.
 * Re-renders when the next one expires, since no response arrives to do it.
 */
export const useOrbitTurnTimeouts = (turns: OrbitTurn[], seen: OrbitPendingSince): OrbitTurn[] => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const now = Date.now();
  const expired: number[] = [];
  let nextExpiry = Infinity;

  turns.forEach(turn => {
    const expiresAt = orbitTurnExpiresAt(turn, seen);

    if (expiresAt === null) {
      return;
    }

    if (expiresAt <= now) {
      expired.push(turn.id);
    } else {
      nextExpiry = Math.min(nextExpiry, expiresAt);
    }
  });

  const expiredKey = expired.join(',');

  useEffect(() => {
    if (nextExpiry === Infinity) {
      return undefined;
    }

    const timer = setTimeout(rerender, nextExpiry - Date.now());

    return () => clearTimeout(timer);
  }, [nextExpiry]);

  return useMemo(() => {
    if (expiredKey === '') {
      return turns;
    }

    const ids = new Set(expiredKey.split(',').map(Number));

    return turns.map(turn => (ids.has(turn.id) ? asTimedOut(turn) : turn));
  }, [turns, expiredKey]);
};

export interface UseOrbitTurnResult {
  turn: OrbitTurn | undefined;
  isLoading: boolean;
  error: any;
  /** Bound SWR mutator: call with no args to revalidate, or with an updater to write the cache. */
  mutate: KeyedMutator<OrbitTurnResponse>;
}

/**
 * Polls one turn until it is terminal, or pending past `ORBIT_PENDING_TURN_LIMIT_MS`, which it then
 * reports as `timeout`. The retry policy is set here rather than inherited, because SWR's default
 * backs off exponentially without a cap and retries a 403/404 that will never succeed. Pass the
 * `seen` the transcript uses, so both time a turn out at the same moment.
 */
export const useOrbitTurn = (id: number | null, seen?: OrbitPendingSince): UseOrbitTurnResult => {
  const [settledId, setSettledId] = useState<number | null>(null);
  const ownSeen = useRef<OrbitPendingSince>(new Map());
  const { data, isLoading, error, mutate } = useSWR<OrbitTurnResponse>(
    id === null ? null : orbitTurnPath(id),
    fetcher,
    {
      // SWR arms the next poll before the response that ends the turn lands. Switching to a number
      // once the turn is terminal or expired changes the effect's deps, which clears that timer.
      refreshInterval: id !== null && id === settledId ? 0 : orbitTurnRefreshInterval,
      onSuccess: response => {
        if (isTerminalOrbitState(response?.data?.state)) {
          setSettledId(id);
        }
      },
      shouldRetryOnError: isRetryableOrbitPollError,
      // A fixed interval, so recovery after an outage takes one interval rather than a backoff
      // step. A hidden tab waits, as SWR's own refresh loop does, without spending a retry.
      onErrorRetry: (_error, _key, _config, revalidate, opts) => {
        if (opts.retryCount > ORBIT_POLL_RETRY_COUNT) {
          return;
        }

        const retry = () => {
          if (document.visibilityState === 'hidden') {
            setTimeout(retry, ORBIT_POLL_INTERVAL_MS);
            return;
          }

          revalidate(opts);
        };

        setTimeout(retry, ORBIT_POLL_INTERVAL_MS);
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );

  const polled = data?.data;
  const expiresAt =
    polled === undefined ? null : orbitTurnExpiresAt(polled, seen ?? ownSeen.current);

  // A turn that stays pending is ended by the clock: an identical response re-renders nothing.
  useEffect(() => {
    if (id === null || expiresAt === null) {
      return undefined;
    }

    const timer = setTimeout(() => setSettledId(id), Math.max(0, expiresAt - Date.now()));

    return () => clearTimeout(timer);
  }, [id, expiresAt]);

  const turn = useMemo(
    () =>
      polled !== undefined && id === settledId && !isTerminalOrbitState(polled.state)
        ? asTimedOut(polled)
        : polled,
    [polled, id, settledId]
  );

  return { turn, isLoading, error, mutate };
};

export interface UseOrbitTurnPagesResult {
  /** Every loaded page flattened, in API order (newest first). */
  turns: OrbitTurn[];
  hasMore: boolean;
  /**
   * At least one page has resolved. `turns.length === 0` alone cannot tell "the server has nothing
   * for this session" from "nothing has been fetched yet", and the two have opposite meanings for a
   * stored session id.
   */
  hasLoaded: boolean;
  /** No page loaded yet and the first request is in flight. */
  isLoading: boolean;
  /** A page beyond the loaded ones is in flight. */
  isLoadingMore: boolean;
  error: any;
  loadMore: () => void;
  mutate: SWRInfiniteKeyedMutator<OrbitListResponse[]>;
}

/**
 * The turn list. Pages accumulate rather than replacing each other, so both readers - the session
 * transcript and the cross-session conversation list - can reach past the backend's default page
 * of 25 instead of silently losing the tail. Pass `null` to skip fetching; stops asking for pages
 * once `meta.next_page` is null.
 */
export const useOrbitTurnPages = (
  params: Omit<OrbitTurnsParams, 'page'> | null
): UseOrbitTurnPagesResult => {
  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite<OrbitListResponse>(
      (index: number, previous: OrbitListResponse | null) => {
        if (params === null || (previous !== null && previous.meta?.next_page === null)) {
          return null;
        }

        return orbitTurnsPath({ size: ORBIT_LIST_PAGE_SIZE, ...params, page: index + 1 });
      },
      fetcher,
      { revalidateOnFocus: false }
    );

  const turns = useMemo(() => (data ?? []).flatMap(page => page.data), [data]);
  const lastPage = data !== undefined && data.length > 0 ? data[data.length - 1] : undefined;
  const hasMore = lastPage !== undefined && typeof lastPage.meta?.next_page === 'number';
  const loadedPages = data?.length ?? 0;
  const loadMore = useCallback(() => setSize(prev => prev + 1), [setSize]);

  return {
    turns,
    hasMore,
    hasLoaded: data !== undefined,
    isLoading,
    isLoadingMore: isValidating && loadedPages > 0 && loadedPages < size,
    error,
    loadMore,
    mutate
  };
};
